import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Copy, Check, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";
import { setupMFA,enableMFA} from "@/services/user";
import api from "@/lib/api";

// Extract secret from otpauth:// URI
const extractSecret = (otpauthUri) => {
  try {
    const match = otpauthUri.match(/[?&]secret=([^&]+)/i);
    return match ? decodeURIComponent(match[1]) : "";
  } catch {
    return "";
  }
};

// Build QR image URL from otpauth URI
const getQrImageUrl = (otpauthUri) => {
  return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
    otpauthUri
  )}`;
};

export const MfaSetup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);

  const [otpauthUri, setOtpauthUri] = useState("");
  const [secret, setSecret] = useState("");
  const [otp, setOtp] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      navigate("/login");
      return;
    }

    const tokenType = localStorage.getItem("token_type") || "Bearer";
    api.defaults.headers.common["Authorization"] = `${tokenType} ${token}`;

    const initMfa = async () => {
      try {
        setLoading(true);
        const res = await setupMFA();
        // response: { success: true, otpauth_uri: "otpauth://..." }
        const uri = res?.otpauth_uri || res?.data?.otpauth_uri || "";

        if (!uri) {
          toast.error("No MFA setup data received");
          return;
        }

        setOtpauthUri(uri);
        setSecret(extractSecret(uri));
      } catch (error) {
        console.error(error);
        toast.error(
          error?.response?.data?.message || "Failed to start MFA setup"
        );
      } finally {
        setLoading(false);
      }
    };

    initMfa();
  }, [navigate]);

  const handleCopy = async () => {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      toast.success("Secret copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy");
    }
  };

  const handleVerify = async (e) => {
  e.preventDefault();
  if (!otp || otp.length !== 6) {
    toast.error("Enter the 6-digit code from your authenticator app");
    return;
  }

  setVerifying(true);
  try {
    const res = await enableMFA(otp);
    console.log("this is enable mfa",res)
    // save any token API returns
    if (res?.mfa_token) {
      localStorage.setItem("mfa_token", res.mfa_token);
    } else {
      localStorage.setItem("mfa_token", "enabled");
    }

    toast.success("MFA enabled successfully");
    navigate("/dashboard");
  } catch (error) {
    console.error(error);
    toast.error(
      error?.response?.data?.message || "Invalid code. Please try again."
    );
  } finally {
    setVerifying(false);
  }
};

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          <p className="text-sm text-slate-600">Setting up MFA...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="border-slate-200 shadow-xl">
          <CardHeader className="space-y-1 text-center pb-6">
            <div className="mx-auto mb-4 h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold">
              Set up two-factor authentication
            </CardTitle>
            <CardDescription>
              Scan the QR code with Google Authenticator, Authy, or similar
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* QR from otpauth_uri */}
            {otpauthUri && (
              <div className="flex justify-center">
                <img
                  src={getQrImageUrl(otpauthUri)}
                  alt="MFA QR Code"
                  className="h-48 w-48 rounded-lg border border-slate-200 bg-white p-2"
                />
              </div>
            )}

            {/* Manual secret */}
            {secret && (
              <div className="space-y-2">
                <Label>Secret key (manual entry)</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={secret}
                    className="font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleCopy}
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Save this key in a safe place in case you lose your phone
                </p>
              </div>
            )}

            <form onSubmit={handleVerify} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp">Verification code</Label>
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  className="text-center text-lg tracking-widest font-semibold"
                  disabled={verifying}
                />
                <p className="text-xs text-slate-500">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>

              <Button type="submit" className="w-full" disabled={verifying}>
                {verifying ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    Complete setup
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="mt-4 text-center text-sm text-muted-foreground">
          PropFirmHouse © 2026. All rights reserved.
        </p>
      </div>
    </div>
  );
};